import { FC, useEffect, useRef, useState } from 'react';

import { RBK_LINK } from 'utils/constants/app';
import { CampaignType } from 'utils/constants/campaign';
import { DOT } from 'utils/constants/common';
import copyLink from 'utils/copy';

import { Button, Input, Link, Text, Tooltip, VSpacing } from '@hh.ru/magritte-ui';
import { CopyOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import { formatToReactComponent } from 'bloko/common/trl';

import useTrls from 'hooks/translation/useTrls';

import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';
import { getCampaignType } from 'components/CpcFlexibleCreate/CpcForm/AdCampaignInformation/getCampaignType';
import { fetchExistAdvCampaigns } from 'components/CpcFlexibleCreate/CpcForm/BannerLink/fetchExistAdvCampaigns';

import { setBannerLink } from 'store/models/createBanner';
import { setCampaignType } from 'store/models/createCampaign';
import { setRequisitesFormOpen } from 'store/models/requisites';
import { SessionType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CpcFlexibleCreate/CpcForm/BannerLink/BannerLink.module.less';

export const BannerLink: FC = () => {
    const { bannerLink, bannerErrors, bannerVacancyId, vacancyName, campaignIdForBanner } = useSelector(
        (state) => state.createBanner
    );
    const campaignType = useSelector((state) => state.createCampaign.campaignType);
    const { sessionType } = useSelector((state) => state.user);
    const isRequisitesFilled = useSelector((state) => state.requisites.isRequisitesFilled);
    const existAdvertisingCampaigns = useSelector((state) => state.existAdvertisingCampaigns);

    const inputRef = useRef<HTMLInputElement>(null);

    const showCheckRequisitesInfo =
        isRequisitesFilled && !existAdvertisingCampaigns && campaignType === CampaignType.Advertising && !!bannerLink;
    const showFillRequisitesInfo = !isRequisitesFilled && campaignType === CampaignType.Advertising && !!bannerLink;

    const [isShowRequisitesInfo, setShowRequisitesInfo] = useState(showCheckRequisitesInfo || showFillRequisitesInfo);

    const trl = useTrls();
    const dispatch = useDispatch();

    const isBannerByVacancy = bannerVacancyId && bannerVacancyId > 0;

    const bannerLinkErrorText = bannerErrors.bannerLink;

    const onChange = (value: string) => {
        if (campaignType) {
            dispatch(setCampaignType(null));
        }
        dispatch(setBannerLink(value.trim()));
    };

    useEffect(() => {
        if (!campaignIdForBanner && isRequisitesFilled) {
            void dispatch(fetchExistAdvCampaigns());
        }
    }, [campaignIdForBanner, dispatch, isRequisitesFilled]);

    useEffect(() => {
        setShowRequisitesInfo(false);
    }, [bannerVacancyId, bannerLink]);

    useEffect(() => {
        if (showCheckRequisitesInfo || showFillRequisitesInfo) {
            setShowRequisitesInfo(true);
        }
    }, [showCheckRequisitesInfo, showFillRequisitesInfo]);

    const infoDescription = (
        <>
            {showFillRequisitesInfo && (
                <>
                    <Text Element="span">
                        {formatToReactComponent(trl('TitlePage.Requisites.description.part1'), {
                            '[0]': (
                                <Link
                                    inline
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={RBK_LINK}
                                    enableVisited={false}
                                >
                                    {trl('TitlePage.Requisites.description.link')}
                                </Link>
                            ),
                        })}
                    </Text>
                    <VSpacing default={8} />
                    <Text>{trl('TitlePage.Requisites.description.part2')}</Text>
                </>
            )}
            {showCheckRequisitesInfo && <Text>{trl('AdCreate.AdForm.BannerLink.infoCheckRequisites')}</Text>}
        </>
    );

    return (
        <MagritteFormField
            label={trl('AdCreate.AdForm.BannerLink.legend')}
            input={
                <>
                    {isBannerByVacancy ? (
                        <div className={styles.copyIconWrapper}>
                            <Link typography="paragraph-1-regular" href={bannerLink} target="_blank">
                                {`${vacancyName} ${DOT} ID ${bannerVacancyId}`}
                            </Link>
                            {sessionType === SessionType.Sudo && (
                                <CopyOutlinedSize24 initialColor="secondary" onClick={() => copyLink(bannerLink)} />
                            )}
                        </div>
                    ) : (
                        <Input
                            ref={inputRef}
                            value={bannerLink}
                            onChange={onChange}
                            placeholder={trl('AdCreate.AdForm.BannerLink.placeholder')}
                            invalid={Boolean(bannerLinkErrorText)}
                            errorMessage={bannerLinkErrorText}
                            data-qa="url"
                            onBlur={() => dispatch(getCampaignType())}
                        />
                    )}
                    <Tooltip
                        visible={isShowRequisitesInfo}
                        activatorRef={inputRef}
                        placement="right-center"
                        closeByClickOutside={false}
                        guardDistanceToActivator={30}
                        showClose
                        onClose={() => setShowRequisitesInfo(false)}
                        buttons={
                            <Button mode="secondary" onClick={() => dispatch(setRequisitesFormOpen(true))}>
                                {trl(
                                    showCheckRequisitesInfo
                                        ? 'AdCreate.AdForm.BannerLink.infoCheckRequisites.button'
                                        : 'TitlePage.Requisites.button'
                                )}
                            </Button>
                        }
                    >
                        {infoDescription}
                    </Tooltip>
                </>
            }
            hint={isBannerByVacancy && trl('AdCreate.AdForm.BannerLink.hint')}
            vSpacingBottom={32}
        />
    );
};
